import React, { useCallback, useEffect, useState } from 'react';
import DecisionTreeInfoWidget from '../DTInfoWidget/DTInfoWidget';
import DummyButton from '../../tools/SearchPage/DummyButton/DummyButton';
import { useDispatch, useSelector } from 'react-redux';
import { getTemplatedCopyTextFromCurrentDecisionNode } from '../../../selectors/templating';
import { NextButton } from '../../common/buttonsComponents';
import { onPasteText, saveNewMessageEditorState } from '../../../actions';
import { getEasyConnectionData } from '../../../actions/easyConnectionActions';
import { trackDecisionTreeTextCopied, SEARCH_ACTION_SOURCES } from '../../../analytics/searchEventAnalytics';
import { sanitizeHtml, messageToClientFormat } from '../../../util';
import Loading from '../../LoadingComponent/LoadingComponent';
import { DraftHelpers } from '../../../util/Draft';
import {
  getDecisionNodeAtPath,
  extractNonHistoricalTicketIdFromState,
  getSelectedDiscountPeriodCouponCode,
  getCurrentShopifyOrderFromSubscription,
  getNewMessagesEditor,
  extractSelectedTicketFromState,
  extractEasyConnectionDataForSelectedTicket,
  getSimplrAccountIdOfSelectedTicket,
  isSelectedTicketTaken,
  extractSelectedInboxTab,
} from '../../../extractFromState';
import * as colors from '../../common/colorConstants';
import MacroText from '../../MacroWizard/MacroText/MacroText';
import { hasUnresolvedEasyConnections, getActiveTriggersInTextMessage } from '../../../util/macroUtil';
import { getMacroById } from '../../../api/macroArticles';
import { addEdgeToDecisionTreePath, setSelectedWizardMacro } from '../../../actions/searchActions';
import { handleError } from 'services/ErrorService/ErrorService';
import {
  BlockingAlert,
  LoadingWrapper,
  MacroContent,
  MacroHeader,
  Icon,
  PageHeader,
  TitleText,
  SubMessage,
  ContentWrapper,
  SubTextLink,
  Container,
} from './DTConfirmationView.styles';
import { ActionsButtonsWrapper } from '../DecisionTree.styles';

const DTConfirmationView = (props) => {
  const dispatch = useDispatch();
  const currentShopifyOrder = useSelector(getCurrentShopifyOrderFromSubscription);
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const templatedCopyText = useSelector(getTemplatedCopyTextFromCurrentDecisionNode);
  const selectedConversationId = useSelector(extractNonHistoricalTicketIdFromState);
  const selectedDiscountPeriodCouponCode = useSelector(getSelectedDiscountPeriodCouponCode);
  const selectedTicket = useSelector(extractSelectedTicketFromState);
  const editorState = useSelector(getNewMessagesEditor);
  const easyConnectionData = useSelector(extractEasyConnectionDataForSelectedTicket);
  const simplrAccountId = useSelector(getSimplrAccountIdOfSelectedTicket);
  const selectedTicketTaken = useSelector(isSelectedTicketTaken);
  const selectedInboxTab = useSelector(extractSelectedInboxTab);

  const [editableMacroContent, setEditableMacroContent] = useState('');
  const [loadingMLData, setLoadingMLData] = useState(true);
  const [loadingMacro, setLoadingMacro] = useState(false);

  const dummyButtonActionTag = decisionNode.dummyButtonActionTag;
  const isLoading = loadingMLData || loadingMacro;

  const { message, showDummyButton = false, showDeliveryStatusLink = false, withEdge = false } = props;

  const loadMacro = useCallback(async () => {
    dispatch(setSelectedWizardMacro(null));
    const macroArticleId = decisionNode?.macroArticle?.objectId;
    // will be present on nodes linked to MacroArticle records
    if (macroArticleId) {
      try {
        setLoadingMacro(true);
        const macroArticle = await getMacroById(macroArticleId, simplrAccountId);
        if (macroArticle) {
          dispatch(setSelectedWizardMacro(macroArticle));
          setLoadingMacro(false);
        } else {
          // keep spinner to block UI
          handleError({
            customMessage: 'The macro attached to this tree has been deactivated',
            popupOnly: true,
          });
        }
      } catch (error) {
        // keep spinner to block UI
        handleError({ error, customMessage: 'There was a problem loading the macro', showPopup: true });
      }
    }
  }, [dispatch, decisionNode, simplrAccountId]);

  const handleGetMLData = useCallback(async () => {
    const sanitizedTemplatedCopyText = sanitizeHtml(templatedCopyText);

    await dispatch(getEasyConnectionData());

    setLoadingMLData(false);
    setEditableMacroContent(sanitizedTemplatedCopyText);
  }, [dispatch, templatedCopyText]);

  useEffect(() => {
    loadMacro();
    handleGetMLData();
  }, [handleGetMLData, loadMacro]);

  const handleReplaceTrigger = ({ updatedMacro, updatedUsedEasyConnections }) => {
    setEditableMacroContent(updatedMacro);
  };

  const copyMacroText = () => {
    const { ticketType, communicationType } = selectedTicket;
    trackDecisionTreeTextCopied(SEARCH_ACTION_SOURCES.COPY_BUTTON);
    const macro = messageToClientFormat(editableMacroContent, ticketType, communicationType);
    const newEditorState = DraftHelpers.appendHtmlToContents(macro, editorState);
    dispatch(saveNewMessageEditorState(selectedConversationId, newEditorState, selectedDiscountPeriodCouponCode));
    dispatch(onPasteText(editableMacroContent));

    if (withEdge) {
      dispatch(addEdgeToDecisionTreePath('copied'));
    }
  };

  const { isDummyActionUsed, handleDummyButtonClick } = DummyButton.useDummyButton({ dummyButtonActionTag });

  const isNextButtonActive =
    !hasUnresolvedEasyConnections(editableMacroContent, easyConnectionData) &&
    isDummyActionUsed() &&
    selectedTicketTaken;

  return isLoading ? (
    <LoadingWrapper>
      <Loading />
    </LoadingWrapper>
  ) : (
    <>
      <Container>
        <PageHeader>
          <Icon src="/images/check_mark_icon.svg" alt="back-button" />
          {message && <TitleText>{message}</TitleText>}
          {showDeliveryStatusLink && (
            <SubTextLink>
              <a
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: colors.black1 }}
                href={currentShopifyOrder.tracking_url}
              >
                Click on this link to see the delivery status
              </a>
            </SubTextLink>
          )}
          <SubMessage>
            {withEdge ? (
              <>
                This is not the end of the flow.
                <br />
                Send the message to the customer and continue.
              </>
            ) : (
              <>
                Please respond to the customer.
                <br /> Remember to personalize your message.
              </>
            )}
          </SubMessage>
        </PageHeader>
        <ContentWrapper>
          <DecisionTreeInfoWidget />
        </ContentWrapper>
        <MacroHeader>
          <TitleText>This is the response that will be copied into your reply box:</TitleText>
        </MacroHeader>
        <MacroContent>
          <MacroText
            easyConnectionData={easyConnectionData}
            editableMacroContent={editableMacroContent}
            onReplaceTrigger={handleReplaceTrigger}
            disableManualCopy={!isNextButtonActive}
          />
        </MacroContent>
        {hasUnresolvedEasyConnections(editableMacroContent, easyConnectionData) && (
          <BlockingAlert>
            Button will activate once hover action for{' '}
            {getActiveTriggersInTextMessage(editableMacroContent, easyConnectionData)} is complete
          </BlockingAlert>
        )}
      </Container>
      <ActionsButtonsWrapper>
        <NextButton
          id="copyTextNextButton"
          active={isNextButtonActive}
          disabled={!isNextButtonActive}
          onClick={copyMacroText}
        >
          {withEdge ? 'COPY MACRO & CONTINUE' : 'COPY MACRO'}
        </NextButton>
        {showDummyButton && (
          <DummyButton
            buttonData={dummyButtonActionTag}
            decisionFlow={true}
            onClick={handleDummyButtonClick}
            selectedInboxTab={selectedInboxTab}
          />
        )}
      </ActionsButtonsWrapper>
    </>
  );
};

export default DTConfirmationView;
